import * as ko from 'knockout';
import { TeamBase } from '../../shared/models/team-base';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { IGame } from '../../shared/interfaces/IGame';
import { RankMethod } from '../../shared/enums/rank-method';
import { NhlGameRecords } from './game-records';
import { Enumerable } from '../../shared/linq';

export class NhlTeam extends TeamBase {
  InPlayoffs: KnockoutObservable<boolean>;
  InWildcard: KnockoutObservable<boolean>;
  DivisionRank: KnockoutObservable<number>;
  WildcardRank: KnockoutObservable<number>;
  PlayoffRank: KnockoutObservable<string>;
  IsDivisionWinner: KnockoutComputed<boolean>;
  WildcardRankText: KnockoutComputed<string>;
  DivisionRankText: KnockoutComputed<string>;
  GroupedSchedule: KnockoutComputed<KnockoutObservable<IGame>[]>;
  subscribers: KnockoutSubscription[] = [];

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number,
    teamLogo?: string
  ) {
    super(teamAbbreviation, regionName, teamName, conference, division, teamID, teamLogo);

    this.InPlayoffs = ko.observable(false);
    this.InWildcard = ko.observable(false);
    this.DivisionRank = ko.observable<number>();
    this.WildcardRank = ko.observable<number>();
    this.PlayoffRank = ko.observable('');

    this.IsDivisionWinner = ko.computed(() => {
      const rankMethod = this.RankMethod();
      return (
        rankMethod === RankMethod.DivisionWinner ||
        rankMethod === RankMethod.DivisionWinnerCoinFlip ||
        rankMethod === RankMethod.DivisionWinnerMultiWay
      );
    });

    this.WildcardRankText = ko.pureComputed(() => {
      this.FakeObs();
      let rank = '';
      if (this.WildcardRank()) {
        rank += this.WildcardRank();
        if (this.RankMethod() === RankMethod.CoinFlip) {
          rank += '*';
        }
      }
      return rank;
    });

    this.DivisionRankText = ko.pureComputed(() => {
      this.FakeObs();
      let rank = '';
      if (this.DivisionRank()) {
        rank += this.DivisionRank();
        if (this.RankMethod() === RankMethod.CoinFlip) {
          rank += '*';
        }
      }
      return rank;
    });

    this.GroupedSchedule = ko.computed(() => {
      this.FakeObs();
      let gameId = 1;
      const retArray: KnockoutObservable<IGame>[] = [];
      const groups = Enumerable.From(this.Schedule())
        .Where((p: KnockoutObservable<IGame>) => typeof p === 'function')
        .GroupBy((p: KnockoutObservable<IGame>) => p().week)
        .OrderBy((p: any) => p.Key())
        .ToArray();
      for (const group of groups) {
        const games = Enumerable.From(group.source)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder())
          .ToArray() as KnockoutObservable<IGame>[];
        for (const game of games) {
          game().gameNum(gameId);
          gameId++;
          retArray.push(game);
        }
      }
      return retArray;
    });
  }

  protected override createGameRecords(): IGameRecords {
    return new NhlGameRecords(
      this.Wins,
      this.Losses,
      this.Ties,
      this.TeamName,
      this.FakeObs
    );
  }

  protected override createConferenceCss(): string {
    this.FakeObs();
    if (this.InPlayoffs()) {
      return 'in-post-season';
    } else if (this.InWildcard()) {
      return 'play-in';
    }
    return 'out-post-season';
  }

  protected override createRankMethodText(method?: RankMethod): string {
    switch (method ?? this.RankMethod()) {
      case RankMethod.RegulationWins:
        return 'Regulation Wins';
      case RankMethod.PointsPct:
        return 'Points Percentage';
      case RankMethod.ROW:
        return 'ROW';
      case RankMethod.TotalWins:
        return 'Total Wins';
      case RankMethod.OverallPoints:
        return 'Overall Points';
      case RankMethod.CoinFlip:
        return 'Coin Flip';
      case RankMethod.HeadToHeadScoring:
        return 'Head To Head Scoring';
      case RankMethod.GoalDifferential:
        return 'Goal Differential';
      case RankMethod.GoalsFor:
        return 'Goals For';
      default:
        return '';
    }
  }

  override initSubscribers(): void {
    const self = this;
    if (!self.isInit) {
      self.subscribers.push(
        self.Losses.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Losses(
                Enumerable.From(self.Losses())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Wins.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.subscribers.push(
        self.Wins.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Wins(
                Enumerable.From(self.Wins())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Losses.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.GameRecords.initSubscribers();
      self.isInit = true;
    }
  }

  override reset(): void {
    this.InPlayoffs(false);
    this.InWildcard(false);
    this.DivisionRank(null);
    this.WildcardRank(null);
    this.PlayoffRank('');
    super.reset();
    for (const subscriber of this.subscribers) {
      subscriber.dispose();
    }
    this.subscribers = [];
    for (const subscriber of (this.GameRecords as NhlGameRecords).subscribers) {
      subscriber.dispose();
    }
    (this.GameRecords as NhlGameRecords).subscribers = [];
    this.isInit = false;
    this.DraftOrderRankMethod(null);
    this.ConferenceRank(null);
  }
}
